import { useEffect, useMemo, useRef, useState } from "react";
import { Chip, Icon, Span, Heading5 } from "@goodfill/ui-components";
import { BondsChart, XAxisOption, YAxisOption } from "./BondsChart";
import { BondsIssuerSelector } from "./BondsIssuerSelector";
import { getIssuersWithPaymentRank, issuerIdWithPaymentRank } from "./getIssuersWithPaymentRank";
import { calculateIssuersColor } from "./calculateIssuersColor";
import { Locale } from "../../../../locale/locale";
import { PricedBond } from "../../PricedBond";
import { IssuerId, CorporateBondId } from "@goodfill/domain-properties";
import { useRecoilValue } from "recoil";
import { userFavoriteBondsState } from "../../../../state";
import "./BondChartView.scss";

const storedSelectedIssuerIdsKey = "selectedIssuerIds";
const getStoredSelectedIssuerIds = () => {
  const storedSelectedIssuerIds = globalThis?.localStorage?.getItem(storedSelectedIssuerIdsKey);
  if (!storedSelectedIssuerIds) {
    return [];
  }
  try {
    const selectedIssuerIds = JSON.parse(storedSelectedIssuerIds);
    if (Array.isArray(selectedIssuerIds)) {
      return selectedIssuerIds;
    }
    return [];
  } catch (_ignore) {
    return [];
  }
};
const setStoredSelectedIssuerIds = (arg: unknown) => {
  if (!Array.isArray(arg)) {
    return;
  }
  if (arg.length === 0) {
    globalThis?.localStorage?.removeItem(storedSelectedIssuerIdsKey);
    return;
  }
  if (arg.every((item) => typeof item === "string" && item !== "")) {
    globalThis?.localStorage?.setItem(storedSelectedIssuerIdsKey, JSON.stringify(arg));
  }
};

const axisLabelMoodyRating = "Moody's";
const axisLabelSpRating = "S&P";
const axisLabelMaturity = "Maturity";
const axisLabelGSpread = "G-Spread";
const axisLabelPrice = "Price";
const axisLabelYieldPercentage = "Yield";

interface Props {
  locale?: Locale;
  pricedBonds: PricedBond[];
  isFavoriteSelected: boolean;
}

export const BondChartView: React.FC<Props> = ({ pricedBonds, isFavoriteSelected }) => {
  const userFavoriteBondsData = useRecoilValue(userFavoriteBondsState);

  const [selectedXAxisOption, setSelectedXAxisOption] = useState<XAxisOption>("maturity");
  const [selectedYAxisOption, setSelectedYAxisOption] = useState<YAxisOption>("yieldPercentage");
  const [selectedXAxisOption2, setSelectedXAxisOption2] = useState<XAxisOption>("maturity");
  const [selectedYAxisOption2, setSelectedYAxisOption2] = useState<YAxisOption>("gSpread");

  const [isComparingCharts, setIsComparingCharts] = useState(false);
  const [compareInRow, setCompareInRow] = useState(false);

  const [issuerSearchString, setIssuerSearchString] = useState("");
  const [selectedBondId, setSelectedBondId] = useState(null as CorporateBondId | null);

  // IMPORTANT HACK: selectedIssuerIds are NOT pure IDs, they also contain PaymentRank,
  //                 see issuerIdWithPaymentRank() for more info
  const [selectedIssuerIds, setSelectedIssuerIds] = useState<IssuerId[]>(getStoredSelectedIssuerIds());

  const onClickCompare = () => {
    setIsComparingCharts((bool: boolean) => !bool);
  };

  const issuers = useMemo(() => getIssuersWithPaymentRank(pricedBonds), [pricedBonds]);

  // This effect will preselect issuers from bonds that are Favorite when Favorite filter is switched ON
  useEffect(() => {
    if (isFavoriteSelected) {
      const bonds = Array.from(pricedBonds.values());
      const filteredBonds = bonds.filter((a) => userFavoriteBondsData.includes(a.bondIdCusip));
      const filteredIssuers = getIssuersWithPaymentRank(filteredBonds);
      const uniqueIssuers = new Set(filteredIssuers.map((a) => a.id));

      setSelectedIssuerIds([...uniqueIssuers]);
    }
  }, [isFavoriteSelected, pricedBonds, userFavoriteBondsData]);

  const onClickClear = () => {
    setIssuerSearchString("");
    setSelectedIssuerIds([]);
  };

  const matchingBondsIssuers = useMemo(() => {
    if (issuerSearchString === "") {
      return issuers;
    }

    return issuers.filter(({ id, name, ticker }) => {
      const normalizedSearchString = issuerSearchString.toLowerCase().trim();

      const searchMatchesTicker = ticker.toLowerCase().includes(normalizedSearchString);
      const searchMatchesName = name.toLowerCase().includes(normalizedSearchString);

      const isSearchMatch = searchMatchesName || searchMatchesTicker;
      const isSelected = selectedIssuerIds.includes(id);

      return isSearchMatch || isSelected;
    });
  }, [issuerSearchString, selectedIssuerIds, issuers]);

  const issuerChips = useMemo(() => {
    return issuers
      .filter(({ id }) => selectedIssuerIds.includes(id))
      .sort(
        // Show chips ordered by user selection.
        ({ id: idA }, { id: idB }) => {
          const a = selectedIssuerIds.indexOf(idA);
          const b = selectedIssuerIds.indexOf(idB);
          if (a < b) {
            return -1;
          }
          if (a > b) {
            return 1;
          }
          return 0;
        },
      )
      .map(({ id, name, paymentRank }) => ({
        id,
        color: calculateIssuersColor(id),
        name,
        onClick: () => {
          setSelectedIssuerIds((ids) => ids.filter((selectedId) => selectedId !== id));
        },
        paymentRank,
      }));
  }, [selectedIssuerIds, issuers]);

  const selectedIssuerBonds = useMemo(() => {
    return pricedBonds
      .map((pricedBond) => ({
        ...pricedBond,
        issuerId: issuerIdWithPaymentRank(pricedBond.issuerId, pricedBond.paymentRank),
      }))
      .filter((pricedBond) => selectedIssuerIds.includes(pricedBond.issuerId));
  }, [selectedIssuerIds, pricedBonds]);

  useEffect(() => {
    setStoredSelectedIssuerIds(selectedIssuerIds);
  }, [selectedIssuerIds, setStoredSelectedIssuerIds]);

  // Reset selectedBondId if user deselect its issuer.
  useEffect(() => {
    if (selectedIssuerBonds.find((pricedBond) => pricedBond.bondIdCusip === selectedBondId) === undefined) {
      setSelectedBondId(null);
    }
  }, [selectedIssuerBonds, selectedBondId]);

  /* ---- Chart responsiveness ---- */
  const MIX_CHART_SIZE = compareInRow ? 320 : 500;
  const [chartsContainerSize, setChartsContainerSize] = useState(MIX_CHART_SIZE);
  const chartsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleResize() {
      const { width } = chartsContainerRef.current?.getBoundingClientRect() ?? {
        width: 0,
      };

      setChartsContainerSize(Math.floor(width));
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const GAP_SIZE_BETWEEN_THEM = 50;
  const leftPaneWidth = 600;
  const numberOfChartsInLine = compareInRow ? 2 : 1;

  const sumOfGapSpace = compareInRow ? GAP_SIZE_BETWEEN_THEM : 0;

  const spaceForChart = (chartsContainerSize - sumOfGapSpace - leftPaneWidth) / numberOfChartsInLine;
  const size = Math.max(MIX_CHART_SIZE, spaceForChart);

  const chartContainerStyle = {
    height: "400px",
    width: `${!isComparingCharts && !compareInRow ? "100%" : `${size}px`}`,
  };

  /* ---------------------------------- */

  return (
    <div className="bg-white gap-2 flex xl:px-20 px-4 md:py-12 py-6 BondChartView_full" ref={chartsContainerRef}>
      <div className="flex flex-col BondChartView_issuersList">
        <Heading5>Bond Issuers</Heading5>
        <BondsIssuerSelector
          issuers={matchingBondsIssuers}
          issuerSearchString={issuerSearchString}
          selectedIssuerIds={selectedIssuerIds}
          setSelectedIssuerIds={setSelectedIssuerIds}
          isComparingCharts={isComparingCharts}
          onClickCompare={onClickCompare}
          onClickClear={onClickClear}
          onClickCompareInRow={() => setCompareInRow(!compareInRow)}
          compareInRow={compareInRow}
        />
      </div>
      <div className="divider lg:pl-9 pl-4" />

      <div className="horizontal-divider pb-5" />

      <div
        className={`lg:pl-6 pl-4 w-full BondChartView_bondIssuersChips ${
          compareInRow && "md:overflow-x-scroll custom-scrollbar md:hide-scrollbar"
        }`}
      >
        {issuerChips.map(({ color, name, onClick, paymentRank }, i) => (
          <Chip size="small" font="medium" primary={true} key={i} className="BondChartView_issuerChipsStyle">
            <div className="flex flex-row items-center gap-3 BondChartView_gap0OnSmall">
              <Icon name="x" onClick={onClick} />
              <div
                style={{
                  height: "14px",
                  width: "14px",
                  borderRadius: "50%",
                  background: color,
                  paddingLeft: "10px",
                }}
              />
              <div className="flex sm:flex-row  justify-center items-center">
                <Span className="BondChartView_chipsLabelFonts">{name}</Span>
                {paymentRank && (
                  <Span size="sm" font="light" className="BondChartView_chipsLabelFonts">
                    {paymentRank}
                  </Span>
                )}
              </div>
            </div>
          </Chip>
        ))}
        <div className={`mt-5 flex ${compareInRow ? "md:flex-row flex-col" : "flex-col"} gap-[50px]`}>
          <BondsChart
            axisLabelMoodyRating={axisLabelMoodyRating}
            axisLabelSpRating={axisLabelSpRating}
            axisLabelMaturity={axisLabelMaturity}
            axisLabelGSpread={axisLabelGSpread}
            axisLabelPrice={axisLabelPrice}
            axisLabelYieldPercentage={axisLabelYieldPercentage}
            selectedXAxisOption={selectedXAxisOption}
            selectedYAxisOption={selectedYAxisOption}
            setSelectedXAxisOption={setSelectedXAxisOption}
            setSelectedYAxisOption={setSelectedYAxisOption}
            pricedBonds={selectedIssuerBonds}
            chartWrapperStyle={chartContainerStyle}
            isRow={compareInRow}
          />
          {isComparingCharts && (
            <BondsChart
              axisLabelMoodyRating={axisLabelMoodyRating}
              axisLabelSpRating={axisLabelSpRating}
              axisLabelMaturity={axisLabelMaturity}
              axisLabelGSpread={axisLabelGSpread}
              axisLabelPrice={axisLabelPrice}
              axisLabelYieldPercentage={axisLabelYieldPercentage}
              selectedXAxisOption={selectedXAxisOption2}
              selectedYAxisOption={selectedYAxisOption2}
              setSelectedXAxisOption={setSelectedXAxisOption2}
              setSelectedYAxisOption={setSelectedYAxisOption2}
              pricedBonds={selectedIssuerBonds}
              chartWrapperStyle={chartContainerStyle}
              isRow={compareInRow}
            />
          )}
        </div>
      </div>
    </div>
  );
};
